import React, { createContext, useState } from "react";
import axios from "axios";

export const ArtContext = createContext();

export const ArtProvider = (props) => {
  const [arts, setArts] = useState([]);
  const [allArts, setAllArts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [error, setError] = useState(null);
  const baseUrl = "https://sawaapi.space";

  const getAuthHeaders = () => {
    const token = localStorage.getItem("jwt");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const fetchRecentArts = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/v1/art/recent`);
      setArts(response.data.data.data); // Update with the recent arts
    } catch (error) {
      setError(error.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Create a delay function
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const fetchArts = async () => {
    try {
      const response = await axios.get(baseUrl + "/api/v1/art");
      setAllArts(response.data.data.data); // Assuming response structure matches
    } catch (error) {
      setError(error.response?.data?.message || "An error occurred");
    } finally {
      // Delay for Loading
      await delay(1000);
      setLoading(false);
    }
  };

  // Fetch a single artwork
  const getArt = async (id) => {
    try {
      const response = await axios.get(baseUrl + `/api/v1/art/${id}`);
      return response.data.data.data;
    } catch (error) {
      throw error.response?.data?.message || "An error occurred";
    }
  };

  // Fetch artworks by category or collection
  const fetchArtsByCategoryOrCollection = async (id) => {
    try {
      setLoading(true);
      let response = await axios.get(`${baseUrl}/api/v1/art?categories=${id}`);
      if (response.data.data.data.length === 0) {
        response = await axios.get(`${baseUrl}/api/v1/art?collections=${id}`);
      }
      setArts(response.data.data.data);
      return response.data.data.data;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // const createArt = async (artData) => {
  //   try {
  //     setLoading(true);

  //     const headers = {
  //       ...getAuthHeaders().headers,
  //       "Content-Type": "multipart/form-data",
  //     };

  //     await axios.post(baseUrl + "/api/v1/art", artData, {
  //       headers: headers,
  //     });
  //     await fetchArts();
  //   } catch (err) {
  //     setError(err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const createArt = async (artData) => {
    try {
      setLoading(true);

      const headers = {
        ...getAuthHeaders().headers,
        "Content-Type": "multipart/form-data",
      };

      await axios.post(baseUrl + "/api/v1/art", artData, {
        headers: headers,
      });
      // alert("Art created successfully");
      await fetchArts();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to create art.";
      setError(errorMessage);
      throw new Error(errorMessage);  // Throwing the error so it can be caught in the form
    } finally {
      setLoading(false);
    }
};


  const updateArt = async (id, updatedData) => {
    try {
      setLoading(true);
      const headers = {
        ...getAuthHeaders().headers,
        "Content-Type": "multipart/form-data",
      };
      
      const response = await axios.patch(
        baseUrl + `/api/v1/art/${id}`,
        updatedData,
        { headers: headers }
      );

      const updatedArt = response.data.data.data;

      setArts((prevArts) =>
        prevArts.map((art) => (art._id === id ? updatedArt : art))
      );
      setAllArts((prevArts) =>
        prevArts.map((art) => (art._id === id ? updatedArt : art))
      );
      await fetchArts();
    } catch (err) {
      console.error("Error updating art:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete an artwork
  const deleteArt = async (id) => {
    try {
      setLoading(true);
      await axios.delete(baseUrl + `/api/v1/art/${id}`, getAuthHeaders());
      setArts((prevArts) => prevArts.filter((art) => art._id !== id));
      setAllArts((prevArts) => prevArts.filter((art) => art._id !== id));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ArtContext.Provider
      value={{
        allArts,
        arts,
        loading,
        error,
        fetchArts,
        getArt,
        fetchArtsByCategoryOrCollection,
        fetchRecentArts,
        createArt,
        updateArt,
        deleteArt,
      }}
    >
      {props.children}
    </ArtContext.Provider>
  );
};
